import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { DESKTOP_MIN_WIDTH } from "~/feature/getUpdates/constants";
import { resolveGetUpdatesAsk } from "~/feature/getUpdates/utils/arbitration.util";
import {
  hasSeenUpdatesDialog,
  incrementPageViews,
  isBandDismissed,
  isNewsletterVisit,
  isSubscribed,
  markAutoAskUsed,
  markNewsletterVisit,
  markUpdatesSeen,
  wasAutoAskUsedThisSession,
  wasBandShownThisSession,
} from "~/feature/getUpdates/utils/getUpdatesStorage";
import { UtmParam } from "~/types/utm";

const DWELL_TRIGGER_MS = 30_000;
const SCROLL_TRIGGER_PX = 800;
const PAGE_VIEW_TRIGGER_COUNT = 2;
const NEWSLETTER_UTM_SOURCE = "newsletter";
const EMAIL_UTM_MEDIUM = "email";

const isFooterFormInViewport = (): boolean => {
  const footerForm = document.querySelector("footer form");

  if (!footerForm) return false;

  const rect = footerForm.getBoundingClientRect();

  return rect.top < window.innerHeight && rect.bottom > 0;
};

const canAutoAsk = (): boolean => {
  if (window.innerWidth < DESKTOP_MIN_WIDTH) return false;

  return resolveGetUpdatesAsk({
    subscribed: isSubscribed(),
    dialogSeen: hasSeenUpdatesDialog(),
    bandDismissed: isBandDismissed(),
    bandShownThisSession: wasBandShownThisSession(),
    autoAskUsedThisSession: wasAutoAskUsedThisSession(),
    landedFromNewsletter: isNewsletterVisit(),
    footerFormInViewport: isFooterFormInViewport(),
    engaged: true,
  }).showAutoDialog;
};

export const useGetUpdatesTriggers = () => {
  const [open, setOpen] = useState(false);
  const openedRef = useRef(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (
      params.get(UtmParam.Source) === NEWSLETTER_UTM_SOURCE ||
      params.get(UtmParam.Medium) === EMAIL_UTM_MEDIUM
    ) {
      markNewsletterVisit();
    }
  }, []);

  const tryOpen = useCallback(() => {
    if (openedRef.current || !canAutoAsk()) return;

    openedRef.current = true;
    setOpen(true);
    markUpdatesSeen();
    markAutoAskUsed();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(tryOpen, DWELL_TRIGGER_MS);

    return () => window.clearTimeout(timeoutId);
  }, [tryOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > SCROLL_TRIGGER_PX) {
        window.removeEventListener("scroll", handleScroll);
        tryOpen();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [tryOpen]);

  useEffect(() => {
    if (incrementPageViews() >= PAGE_VIEW_TRIGGER_COUNT) {
      tryOpen();
    }
  }, [pathname, tryOpen]);

  const handleClose = useCallback(() => setOpen(false), []);

  return { open, handleClose };
};
